import { Plus } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Can } from "@/lib/authorization";
import { ErrorAlert } from "@/lib/components/error-alert";
import { BaseNavigationBar, SettingsBackButton } from "@/lib/components/navigation";
import { PageContent } from "@/lib/components/page-content";
import { SettingsSearchTrigger } from "../../search/components/settings-search-trigger";
import { HolidayBulkDeleteDialog } from "./components/holiday-bulk-delete-dialog";
import { HolidayDeleteDialog } from "./components/holiday-delete-dialog";
import { HolidayForm } from "./components/holiday-form";
import { HolidayList } from "./components/holiday-list";
import { useHolidays } from "./hooks";
import { useHolidayUIStore } from "./store/use-holiday-ui-store";

export function HolidaysSettingsPage() {
  const { t } = useTranslation("settings");

  const {
    activeTab,
    setActiveTab,
    isCreateDialogOpen,
    isEditDialogOpen,
    isDeleteDialogOpen,
    isBulkDeleteDialogOpen,
    openCreateDialog,
  } = useHolidayUIStore();

  const { error } = useHolidays();

  return (
    <>
      <BaseNavigationBar
        title={t("sections.holidays.title")}
        subtitle={t("sections.holidays.description")}
        leadElement={<SettingsBackButton key="back-button" />}
        trailingElements={[
          <SettingsSearchTrigger key="search-trigger" />,
          <Can I="create" a="Holiday" key="create-button">
            <Button onClick={openCreateDialog}>
              <Plus className="w-4 h-4 mr-2" />
              {t("holidays.addHoliday")}
            </Button>
          </Can>,
        ]}
      />

      <PageContent>
        <div className="space-y-6">
          <ErrorAlert error={error} />

          <Tabs
            value={activeTab}
            onValueChange={(value) => setActiveTab(value as "future" | "archive")}
          >
            <TabsList className="mb-2">
              <TabsTrigger value="future">{t("holidays.upcoming")}</TabsTrigger>
              <TabsTrigger value="archive">{t("holidays.past")}</TabsTrigger>
            </TabsList>

            <TabsContent value="future" className="space-y-4">
              <HolidayList timeFilter="future" />
            </TabsContent>

            <TabsContent value="archive" className="space-y-4">
              <HolidayList timeFilter="past" />
            </TabsContent>
          </Tabs>
        </div>
      </PageContent>

      {/* Dialogs */}
      {isCreateDialogOpen && <HolidayForm />}
      {isEditDialogOpen && <HolidayForm />}
      {isDeleteDialogOpen && <HolidayDeleteDialog />}
      {isBulkDeleteDialogOpen && <HolidayBulkDeleteDialog />}
    </>
  );
}
